/**
 * Confirm Dialog Utility
 * A Promise-based Bootstrap modal confirmation dialog.
 *
 * Usage:
 *   const confirmed = await Confirm.show('Are you sure?');
 *
 *   const confirmed = await Confirm.show({
 *       title: 'Cancel Appointment',
 *       message: 'This cannot be undone.',
 *       confirmText: 'Yes, Cancel',
 *       confirmVariant: 'danger'
 *   });
 *
 * Returns: Promise<boolean> — resolves true on confirm, false on cancel/dismiss.
 */
window.Confirm = {
    /**
     * Show a confirmation dialog.
     * @param {string|Object} options - Message string or options object.
     * @param {string} options.title - Modal title (default: 'Confirm')
     * @param {string} options.message - Body message (default: 'Are you sure?')
     * @param {string} options.confirmText - Confirm button label (default: 'Confirm')
     * @param {string} options.confirmVariant - Bootstrap button variant for confirm button (default: 'primary')
     * @param {string} options.cancelText - Cancel button label (default: 'Cancel')
     * @param {string} options.icon - Bootstrap icon class for the header (e.g. 'bi bi-exclamation-triangle text-danger')
     * @returns {Promise<boolean>}
     */
    show(options = {}) {
        if (typeof options === 'string') {
            options = { message: options };
        }

        const config = {
            title: options.title || 'Confirm',
            message: options.message || 'Are you sure?',
            confirmText: options.confirmText || 'Confirm',
            confirmVariant: options.confirmVariant || 'primary',
            cancelText: options.cancelText || 'Cancel',
            icon: options.icon || null
        };

        return new Promise((resolve) => {
            const modalEl = document.createElement('div');
            modalEl.className = 'modal fade';
            modalEl.tabIndex = -1;
            modalEl.setAttribute('aria-modal', 'true');
            modalEl.setAttribute('role', 'dialog');

            modalEl.innerHTML = `
                <div class="modal-dialog modal-dialog-centered">
                    <div class="modal-content">
                        <div class="modal-header">
                            ${config.icon ? `<i class="${config.icon} me-2"></i>` : ''}
                            <h5 class="modal-title">${config.title}</h5>
                            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div class="modal-body">
                            <p class="mb-0">${config.message}</p>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">${config.cancelText}</button>
                            <button type="button" class="btn btn-${config.confirmVariant} confirm-dialog-confirm-btn">${config.confirmText}</button>
                        </div>
                    </div>
                </div>
            `;

            document.body.appendChild(modalEl);

            const modal = new bootstrap.Modal(modalEl, { keyboard: true });
            let confirmed = false;

            modalEl.querySelector('.confirm-dialog-confirm-btn').addEventListener('click', () => {
                confirmed = true;
                modal.hide();
            });

            modalEl.addEventListener('hidden.bs.modal', () => {
                modalEl.remove();
                resolve(confirmed);
            });

            modal.show();
        });
    }
};
